import React, { FC, ReactElement, ReactNode, useCallback, useEffect, useState } from "react";
import { Rotate, Toggler } from "@baseTypes/baseType";
import * as Highcharts from "highcharts";
import cn from "classnames";
import st from './Legend.module.scss';
import { LoggerFactory } from "@infobis/as-logger";
import { isEmpty } from "lodash";

const log = LoggerFactory.getLogger("legend")

export interface LegendRow {
    id: string | string[]
    color: string
    values: ReactElement[]
}

type RowVariant = 'default' | 'nameValue'

export interface LegendOptions {
    className?: string
    headersR?: ReactElement[]
    rows: LegendRow[]
    rotate?: Rotate
    height?: number
    chartApi?: Highcharts.Chart,
    toggler?: Toggler
    onClick?: (el: LegendRow) => any
    rowCls?: string,
    headerCls?: string
    bodyCls?: string
    disabledByDefaultOptions?: string[]
    legendRowCls?: string
    rowVariant?: RowVariant
    width?: number
    unselectable?: boolean
    rowRenderer?: (row: LegendRow) => ReactNode
    gap?: number
}

const Legend: FC<LegendOptions> = (props) => {
    const {
        rotate = Rotate.Column,
        height,
        headersR = [],
        chartApi,
        onClick = () => {
        },
        toggler,
        disabledByDefaultOptions = [],
        rowCls,
        headerCls,
        bodyCls,
        rowVariant = 'default',
        rows,
        width,
        unselectable = false,
        rowRenderer,
        gap
    } = props

    let handler = onClick

    if (toggler && chartApi) {
        if (toggler === Toggler.series) {
            handler = (el: LegendRow) => toggleSeries(chartApi?.series, el.id)
        } else if (toggler === Toggler.points) {
            handler = (el: LegendRow) => toggleSeries(chartApi?.series[0].points, el.id)
        }
    }

    const [legendActiveMap, setLegendActiveMap] = useState<{ [key: string]: boolean }>({})

    useEffect(() => {
        setLegendActiveMap(
            rows.reduce((acc, i) =>
                ({ [String(i.id)]: !disabledByDefaultOptions.includes(i.id.toString()), ...acc }), {}))
    }, [rows])

    const getRowClassNames = (id: string) =>
        cn(st.legend__body_row, { [st.legend__body_row_unactive]: !legendActiveMap[id] }, rowCls)

    const clickHandler = (item: LegendRow) => () => {
        if (!unselectable) {
            handler(item)
            setLegendActiveMap((state) => ({ ...state, [item.id.toString()]: !legendActiveMap[item.id.toString()] }))
        }
    }

    const [overflow, setOverflow] = useState(false)

    const bodyRef = useCallback((node: HTMLElement | null) => {
        if (node && node.scrollHeight > node.clientHeight) setOverflow(true)
    }, [])

    return (
        <div className={cn(st.legend, st[`legend--${rowVariant}`])} style={{ width }}>
            {!isEmpty(headersR) &&
                <div className={cn(st.legend__header, overflow && st["legend__header--overflow"], headerCls)}>
                    <div className={cn(st.legend__body_row, st["legend__header_row"], rowCls)}>
                        {React.Children.map(headersR, (child, idx) =>
                            React.cloneElement(child, { key: idx }))}
                    </div>
                </div>
            }
            <div
                className={cn(st.legend__body, st[`legend__body--${rotate}`], overflow && st["legend__body--overflow"], height && st.legend__body_fixed, bodyCls)}
                style={{ height: height ? height + "px" : "auto", gap }}
                ref={bodyRef}
            >
                {rows.map(item =>
                    <div key={item.id.toString()} className={getRowClassNames(String(item.id))} onClick={clickHandler(item)}>
                        {rowRenderer ? rowRenderer(item) : item.values.map((v, idx) => <div
                            key={item.id.toString() + idx}>{v}</div>)}
                    </div>
                )}
            </div>
        </div>
    )
}

export const toggleSeries = (data: (Highcharts.Series | Highcharts.Point)[] | undefined, id: string | string[]) => {
    const series = data?.filter(p => Array.isArray(id) ? id.some(x => p.options.id === x) : p.options.id === id)
    if (series === undefined || series.length === 0) {
        log.warn("Unable to toggle the data because the corresponding point was not found. Please check the IDs of the points.", data, id)
    } else {
        series.forEach((series) => {
            series.visible ? series.setVisible(false, true) : series.setVisible(true, true)
        })
    }


}

export default Legend;